import { action, computed } from 'mobx';
import i18next from 'i18next';

import { AppManager, Input, Variables } from 'core/index';

import { ThreadConnManager } from 'apps/forum/main/appManager';
import { ProjectConnManager } from 'apps/project/connManager';
import { StoryConnManager } from 'apps/story/main/appManager';
import { CollectionConnManager } from 'apps/collection/main/connManager';

import { ChannelApp, ChannelModel } from 'apps/channel/app';
import {
  ChannelMutation,
  ChannelQuery,
  ChannelProjectsQuery,
  ChannelStoriesQuery,
  ChannelCollectionsQuery,
  ChannelThreadsQuery,
} from 'apps/channel/gql';

export class ChannelAppManager extends AppManager {
  static registryName = 'ChannelAppManager';
  constructor(parent, init = {}) {
    super(parent, init);
    this.AppRef('channel', ChannelApp);

    this.variables = new Variables({
      slug: () => this.params.channel,
      // threads
      threadCursor: () => this.threadsConn.endCursor,
      threadSortBy: () => this.threadsConn.sortBy,
      threadBatchSize: () => this.threadsConn.batchSize,
      projectsCursor: () => null,
      projectsSortBy: () => this.projectsConn.sortBy,
      projectsCount: () => this.projectsConn.variables.batchSize,
      storiesCursor: () => null,
      storiesSortBy: () => this.storiesConn.sortBy,
      storiesCount: () => {
        if (
          this.storiesConn.arrayApp.length > this.storiesConn.initialBatchSize
        ) {
          return this.storiesConn.batchSize;
        }
        return this.storiesConn.initialBatchSize;
      },
      collectionsCursor: () => null,
      collectionsSortBy: () => this.collectionsConn.sortBy,
      collectionsCount: () => {
        if (
          this.collectionsConn.arrayApp.length >
          this.collectionsConn.initialBatchSize
        ) {
          return this.collectionsConn.batchSize;
        }
        return this.collectionsConn.initialBatchSize;
      },
    });

    this.threadsConn = new ThreadConnManager(this, {
      slug: init.params.channel,
      sortOptions: [],
      batchSize: 5,
      loadMoreQuery: ChannelThreadsQuery,
    });
    this.projectsConn = new ProjectConnManager(this, {
      sortBy: 'stars',
      initialBatchSize: 12,
      batchSize: 12,
      loadMoreQuery: ChannelProjectsQuery,
      filterBy: [
        ['Project.canAppearOnHome', 'true'],
        ['Project.isPrivate', 'false'],
      ],
    });
    this.storiesConn = new StoryConnManager(this, {
      sortBy: '-likes',
      sortOptions: [
        ['-likes', i18next.t('Likes')],
        ['recent', i18next.t('Recent')],
        ['pageviews', i18next.t('Views')],
      ],
      initialBatchSize: 12,
      batchSize: 12,
      loadMoreQuery: ChannelStoriesQuery,
    });
    this.collectionsConn = new CollectionConnManager(this, {
      sortBy: 'recent',
      loadMoreQuery: ChannelCollectionsQuery,
    });

    this.variables.onChange(['slug'], this.NewSlug);

    this.channelInput = new Input({
      id: () => this.channel.id,
      name: () => this.channel.name,
      description: () => this.channel.description,
      image: () => this.channel.image,
    });
  }

  @computed get loading() {
    return this.queries.get('channel')?.loading;
  }

  @computed get notFound() {
    return this.queries.get('channel')?.notFound;
  }

  @action NewSlug = () => {
    this.threadsConn.NewVariables();
    this.projectsConn.NewUsername();
    this.storiesConn.NewUsername();
    this.collectionsConn.NewUsername();
    this.NewChannelQuery();
  };

  @action NewChannelQuery = () => {
    const q = new ChannelQuery(this, {
      queryKey: 'channel',
      onSysErr: this.onInitiativeError,
    });
    q.process(channel => ChannelModel.Load(channel), this.ChannelUpdate);
    q.process(this.threadsConn.process, this.ThreadUpdate, {
      subPath: 'threads',
    });
    q.process(this.projectsConn.process, this.ProjectsUpdate, {
      subPath: 'projects',
    });
    q.process(this.storiesConn.process, this.StoriesUpdate, {
      subPath: 'stories',
    });
    q.process(this.collectionsConn.process, this.CollectionsUpdate, {
      subPath: 'collections',
      loadMoreQuery: ChannelCollectionsQuery,
    });
    q.query();
  };

  @action ChannelUpdate = channelModel => {
    this.channel = channelModel;
  };

  @action ThreadUpdate = (threads, query) => {
    this.threadsConn.pushResultIfFirst(threads, query);
  };

  @action ProjectsUpdate = (projects, query) => {
    this.projectsConn.pushResultIfFirst(projects, query);
  };

  @action StoriesUpdate = (stories, query) => {
    this.storiesConn.pushResultIfFirst(stories, query);
  };

  @action CollectionsUpdate = (collections, query) => {
    this.collectionsConn.pushResultIfFirst(collections, query);
  };

  @action SubmitChannel = options => {
    this.mutations.push(ChannelMutation.New(this.channelInput, options));
  };
}
